import {LoadingButton} from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItem,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {ReactNode, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useAppDispatch} from '../../../hooks/redux';
import {Map} from '../../../interfaces/Map';
import reduxActions from '../../../redux/actions';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import SnackbarMessages from '../../common/SnackbarMessages';

interface MapItemDeleteButtonProps {
  item: Map;
  prefetch?: boolean;
  component?: React.FC;
  componentProps?: any;
  onDeleted?: Function;
  children?: ReactNode;
}

export const MapItemDeleteButton: React.FC<MapItemDeleteButtonProps> = ({
  item,
  component = ListItem,
  componentProps = {},
  onDeleted,
  children,
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const Component = component;

  const [newItem] = useState<Map>(item);
  const [isOpened, setIsOpened] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = () => setIsOpened(true);
  const closeModal = () => setIsOpened(false);
  const reduxDispatch = useAppDispatch();

  const submit = async () => {
    setIsSubmitting(true);

    try {
      await API.delete(`${apiBaseUrl}/map/${newItem.id}`);
      const message = 'Map successfully deleted';
      enqueueSnackbar(message, {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      reduxDispatch(reduxActions.assets.fetchMaps);
      onDeleted?.();
      closeModal();
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_0cd2a848");
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <Component {...componentProps} onClick={openModal}>
        {children}
      </Component>

      <Dialog open={isOpened} onClose={closeModal}>
        <DialogTitle>Delete Map</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this map?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>

          <LoadingButton
            variant="contained"
            loading={isSubmitting}
            autoFocus
            onClick={() => submit()}
            color="error"
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
